import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useToast } from '../contexts/ToastContext';

export function useUserManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const addToast = useToast();

  // Fetch users with all related data
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // First get user profiles
      const { data: profiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profileError) throw profileError;

      // Then get subscriptions with tiers for each user
      let usersWithSubscriptions = [];
      
      if (profiles && profiles.length > 0) {
        const userIds = profiles.map(p => p.user_id);
        
        const { data: subscriptions, error: subError } = await supabase
          .from('user_subscriptions')
          .select(`
            *,
            subscription_tiers (
              name,
              display_name,
              price_monthly,
              price_annual
            )
          `)
          .in('user_id', userIds);

        if (subError) {
          console.warn('Could not fetch subscriptions:', subError);
        }

        // Merge profiles with their subscriptions
        usersWithSubscriptions = profiles.map(profile => {
          const userSubscriptions = subscriptions?.filter(sub => sub.user_id === profile.user_id) || [];
          return {
            ...profile,
            user_subscriptions: userSubscriptions
          };
        });
      } else {
        usersWithSubscriptions = profiles || [];
      }

      // Get real email addresses from auth.users table
      const userIds = usersWithSubscriptions?.map(user => user.user_id) || [];
      
      if (userIds.length > 0) {
        // Use RPC function to get auth user emails (admins only)
        try {
          const { data: rpcUsers, error: rpcError } = await supabase
            .rpc('get_user_emails', { user_ids: userIds });
          
          if (!rpcError && rpcUsers) {
            const usersWithEmail = usersWithSubscriptions.map(user => {
              const authUser = rpcUsers.find(au => au.id === user.user_id);
              return {
                ...user,
                email: authUser?.email || `user-${user.user_id.slice(0, 8)}@example.com`,
                email_confirmed_at: authUser?.email_confirmed_at || null,
                last_sign_in_at: authUser?.last_sign_in_at || null
              };
            });
            setUsers(usersWithEmail);
          } else {
            console.warn('RPC function failed:', rpcError);
            // Fallback to placeholder emails
            const usersWithEmail = usersWithSubscriptions.map(user => ({
              ...user,
              email: `user-${user.user_id.slice(0, 8)}@example.com`,
              email_confirmed_at: null,
              last_sign_in_at: null
            }));
            setUsers(usersWithEmail);
          }
        } catch (error) {
          console.warn('Error calling get_user_emails RPC:', error);
          // Use placeholder emails as final fallback
          const usersWithEmail = usersWithSubscriptions.map(user => ({
            ...user,
            email: `user-${user.user_id.slice(0, 8)}@example.com`,
            email_confirmed_at: null,
            last_sign_in_at: null
          }));
          setUsers(usersWithEmail);
        }
      } else {
        setUsers(usersWithSubscriptions || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      addToast?.('Error fetching users: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  // Filter and sort users together
  useEffect(() => {
    let result = [...users];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(user => {
        return (
          user.email?.toLowerCase().includes(query) ||
          user.first_name?.toLowerCase().includes(query) ||
          user.last_name?.toLowerCase().includes(query) ||
          user.username?.toLowerCase().includes(query) ||
          user.user_subscriptions?.[0]?.subscription_tiers?.name?.toLowerCase().includes(query) ||
          user.user_subscriptions?.[0]?.status?.toLowerCase().includes(query)
        );
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      const { key, direction } = sortConfig;
      
      let aValue = a[key];
      let bValue = b[key];

      // Handle nested values
      if (key === 'subscription_tier') {
        aValue = a.user_subscriptions?.[0]?.subscription_tiers?.name || '';
        bValue = b.user_subscriptions?.[0]?.subscription_tiers?.name || '';
      } else if (key === 'subscription_status') {
        aValue = a.user_subscriptions?.[0]?.status || '';
        bValue = b.user_subscriptions?.[0]?.status || '';
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(result);
  }, [users, searchQuery, sortConfig]);

  // Create user
  const createUser = async (userData) => {
    try {
      // Use secure RPC function for admin user creation with audit logging
      const { data, error } = await supabase.rpc('admin_create_user', {
        user_email: userData.email,
        user_first_name: userData.first_name,
        user_last_name: userData.last_name,
        user_username: userData.username,
        user_password: userData.password,
        user_avatar_type: userData.avatar_type || 'initials',
        user_avatar_color: userData.avatar_color || '#6366f1',
        user_avatar_initials: userData.avatar_initials,
        subscription_tier_id: userData.subscription_tier_id || null,
        billing_cycle: userData.billing_cycle || 'monthly',
        admin_reason: 'User created via admin UI'
      });

      if (error) throw error;

      await fetchUsers();
      addToast?.('User created successfully with audit logging', 'success');
      return { id: data.user_id };
    } catch (err) {
      console.error('Error creating user:', err);
      addToast?.('Error creating user: ' + err.message, 'error');
      throw err;
    }
  };

  // Update user
  const updateUser = async (userId, updates) => {
    try {
      console.log('Updating user:', userId, 'with data:', updates);
      
      // Use secure RPC function for admin user updates with audit logging
      const { error } = await supabase.rpc('admin_update_user', {
        target_user_id: userId,
        user_first_name: updates.first_name,
        user_last_name: updates.last_name,
        user_username: updates.username,
        user_avatar_type: updates.avatar_type,
        user_avatar_color: updates.avatar_color,
        user_avatar_initials: updates.avatar_initials,
        subscription_tier_id: updates.subscription_tier_id || null,
        billing_cycle: updates.billing_cycle,
        admin_reason: 'User updated via admin UI'
      });

      if (error) {
        console.error('User update error:', error);
        throw error;
      }

      console.log('User updated successfully via RPC');
      await fetchUsers();
      addToast?.('User updated successfully with audit logging', 'success');
    } catch (err) {
      console.error('Error updating user:', err);
      addToast?.('Error updating user: ' + err.message, 'error');
      throw err;
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      // Use secure RPC function for admin user deletion with audit logging
      const { error } = await supabase.rpc('admin_delete_user', {
        target_user_id: userId,
        admin_reason: 'User deleted via admin UI'
      });
      
      if (error) throw error;

      await fetchUsers();
      addToast?.('User deleted successfully with audit logging', 'success');
    } catch (err) {
      console.error('Error deleting user:', err);
      addToast?.('Error deleting user: ' + err.message, 'error');
      throw err;
    }
  };

  // Clone user
  const cloneUser = async (sourceUser) => {
    try {
      const clonedData = {
        email: `clone-${Date.now()}@example.com`, // Generate unique email
        first_name: sourceUser.first_name,
        last_name: sourceUser.last_name,
        username: `${sourceUser.username}-clone-${Date.now()}`,
        subscription_tier_id: sourceUser.user_subscriptions?.[0]?.subscription_tier_id,
        billing_cycle: sourceUser.user_subscriptions?.[0]?.billing_cycle,
        avatar_type: sourceUser.avatar_type,
        avatar_color: sourceUser.avatar_color,
        avatar_initials: sourceUser.avatar_initials
      };

      return await createUser(clonedData);
    } catch (err) {
      console.error('Error cloning user:', err);
      addToast?.('Error cloning user: ' + err.message, 'error');
      throw err;
    }
  };

  // Initialize
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    filteredUsers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    sortConfig,
    setSortConfig,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    cloneUser
  };
}
